import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { BoundingBox, MapLayer, Position } from 'components/map/types';
import type { RootState } from 'store/reducers';

import type { Camera, Vehicle } from 'API';

export interface RouteLocation {
    id: string;
    label: string;
    position: Position;
}

interface VehicleMapState {
    showHubs: boolean;
    mapContainerSizeTimestamp: number;
    mapLayers: MapLayer[];
    boundingBox?: BoundingBox;
}

interface PagesState {
    vehicleDetails: {
        open: boolean;
        reference: string | undefined;
    };
}

interface SelectedVehicleState {
    selectedVIN: Vehicle['vin'] | undefined;
    pvlTriggered: boolean | undefined;
}

export enum SelectedDeviationTab {
    ACTIVE = 'active',
    RESOLVED = 'resolved',
}

export interface AppState {
    sessionExpiredAcknowledged: boolean;
    map: VehicleMapState;
    selectedVehicleState: SelectedVehicleState;
    followSelectedVehicle: boolean; // todo: review variable name
    isFullMissionRouteVisible: boolean; // todo: review variable name
    pages: PagesState;
    isCameraStreamMaximized: boolean;
    selectedCamera?: Camera;
    selectedDeviationTab: SelectedDeviationTab;
    actualDeviationParentId: string | undefined;
}

const initialState: AppState = {
    sessionExpiredAcknowledged: false,
    map: {
        boundingBox: undefined,
        showHubs: true,
        mapContainerSizeTimestamp: Date.now(),
        mapLayers: [],
    },
    selectedVehicleState: {
        selectedVIN: undefined,
        pvlTriggered: undefined,
    },
    followSelectedVehicle: false,
    isFullMissionRouteVisible: false,
    pages: {
        vehicleDetails: {
            open: false,
            reference: undefined,
        },
    },
    isCameraStreamMaximized: false,
    selectedCamera: undefined,
    selectedDeviationTab: SelectedDeviationTab.ACTIVE,
    actualDeviationParentId: undefined,
};

export const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        resetAppState: () => initialState,
        hideSessionExpiredDialog: (state: AppState) => {
            state.sessionExpiredAcknowledged = true;
        },
        setSelectedVehicle: (
            state: AppState,
            action: PayloadAction<{ selectedVIN?: Vehicle['vin']; pvlTriggered?: boolean }>
        ) => {
            state.selectedVehicleState = {
                selectedVIN: action.payload.selectedVIN,
                pvlTriggered: action.payload.pvlTriggered,
            };
        },
        clearSelectedVehicle: (state: AppState) => {
            state.selectedVehicleState.selectedVIN = undefined;
            state.selectedVehicleState.pvlTriggered = undefined;
        },
        setFollowSelectedVehicle: (state: AppState, action: PayloadAction<boolean>) => {
            state.followSelectedVehicle = action.payload;
        },
        setIsFullMissionRouteVisible: (state: AppState, action: PayloadAction<boolean>) => {
            state.isFullMissionRouteVisible = action.payload;
        },
        setIsVehicleDetailsOpen: (state: AppState, action: PayloadAction<boolean>) => {
            state.pages.vehicleDetails = { ...state.pages.vehicleDetails, open: action.payload };
        },
        setBoundingBox: (state: AppState, action: PayloadAction<BoundingBox | undefined>) => {
            state.map.boundingBox = action.payload;
        },
        setMapLayers: (state: AppState, action: PayloadAction<MapLayer[]>) => {
            state.map.mapLayers = action.payload;
        },
        setShowHubs: (state: AppState, action: PayloadAction<boolean>) => {
            state.map.showHubs = action.payload;
        },
        setIsCameraStreamMaximized: (state: AppState, action: PayloadAction<boolean>) => {
            state.isCameraStreamMaximized = action.payload;
        },
        setSelectedCamera: (state: AppState, action: PayloadAction<Camera>) => {
            state.selectedCamera = action.payload;
        },
        setVehicleDetailsPageReference: (state: AppState, action: PayloadAction<string | undefined>) => {
            state.pages.vehicleDetails.reference = action.payload;
        },
        setSelectedDeviationTab: (state: AppState, action: PayloadAction<SelectedDeviationTab>) => {
            state.selectedDeviationTab = action.payload;
        },
        setActualDeviationParentId: (state: AppState, action: PayloadAction<string | undefined>) => {
            state.actualDeviationParentId = action.payload;
        },
    },
});

export const {
    resetAppState,
    hideSessionExpiredDialog,
    setSelectedVehicle,
    clearSelectedVehicle,
    setIsVehicleDetailsOpen,
    setBoundingBox,
    setMapLayers,
    setShowHubs,
    setFollowSelectedVehicle,
    setIsFullMissionRouteVisible,
    setIsCameraStreamMaximized,
    setSelectedCamera,
    setVehicleDetailsPageReference,
    setSelectedDeviationTab,
    setActualDeviationParentId,
} = appSlice.actions;

// map selectors
export const getBoundingBox = (state: RootState) => state.app.map.boundingBox;
export const getMapContainerSizeTimestamp = (state: RootState) => state.app.map.mapContainerSizeTimestamp;
export const getMapLayers = (state: RootState) => state.app.map.mapLayers;
export const getShowHubs = (state: RootState) => state.app.map.showHubs;

export const getSessionExpiredAcknowledged = (state: RootState) => state.app.sessionExpiredAcknowledged;
export const getSelectedVehicle = (state: RootState) => state.app.selectedVehicleState; // todo: rename action to getSelectedVehicleState
export const getIsPVLTriggered = (state: RootState) => state.app.selectedVehicleState.pvlTriggered;
export const getFollowSelectedVehicle = (state: RootState) => state.app.followSelectedVehicle;
export const getIsFullMissionRouteVisible = (state: RootState) => state.app.isFullMissionRouteVisible;
export const getSelectedCamera = (state: RootState) => state.app.selectedCamera;
export const getIsCameraStreamMaximized = (state: RootState) => state.app.isCameraStreamMaximized;
export const getSelectedDeviationTab = (state: RootState) => state.app.selectedDeviationTab;
export const getActualDeviationParentId = (state: RootState) => state.app.actualDeviationParentId;

// window selectors
export const getIsVehicleDetailsOpen = (state: RootState) => state.app.pages.vehicleDetails.open;
export const getVehicleDetailsPageReference = (state: RootState) => state.app.pages.vehicleDetails.reference;

export default appSlice.reducer;
